// utils/dashboardUtils.ts or transformer/dashboardUtils.ts

import React from "react";
import { ISection, IAnalysis, ISubSection, IGlobalDimension, IDashboardCacheData, IDashboardFilterDropdown, NumberFormat, IFilterValues } from "../models/interfaces";
import { AppDispatch } from "src/store";
import { sanitizeForLog } from "src/utils/security";
import { setDefaultFilter, setOverridenFilter } from "../states/quantDashboardSlice";
import numeral from "numeral";

/**
 * Constants for dashboard configuration
 */
export const DASHBOARD_CONSTANTS = {
	MAX_GLOBAL_DIMENSIONS: 2,
	SCROLL_DELAY_MS: 100,
	HIGHLIGHT_DURATION_MS: 2000,
	MAX_TABS: 5,
	MAX_TRUNCATE_LENGTH: 48,
	MIN_TRUNCATE_LENGTH: 32,
} as const;

/**
 * Position types for tab operations
 */
export enum PositionType {
	LEFT = "left",
	RIGHT = "right",
}

/**
 * Safely gets the first analysis from a section's sub-sections
 * @param section - The section to extract analysis from
 * @returns Object containing the first analysis and sub-section, or null if not found
 */
export const getFirstAnalysisFromSection = (
	section: ISection | undefined,
): {
	analysis: IAnalysis | null;
	subSection: ISubSection | null;
} => {
	if (!section?.sub_sections?.length) {
		return { analysis: null, subSection: null };
	}

	const firstSubSection = section.sub_sections[0];
	if (!firstSubSection?.analyses?.length) {
		return { analysis: null, subSection: null };
	}

	return {
		analysis: firstSubSection.analyses[0],
		subSection: firstSubSection,
	};
};

/**
 * Creates dimension structure with placeholders for global filters
 * @param mergedGlobalFilters - Array of merged global filters
 * @param maxDimensions - Maximum number of dimensions to create
 * @returns Array of dimensions with placeholders
 */
export const createDimensionsWithPlaceholders = (mergedGlobalFilters: any[], maxDimensions: number = DASHBOARD_CONSTANTS.MAX_GLOBAL_DIMENSIONS): IGlobalDimension[] => {
	const dimensions: IGlobalDimension[] = [];

	// If no global filters, return placeholders with order 1 and 2
	if (!mergedGlobalFilters || mergedGlobalFilters.length === 0) {
		for (let i = 1; i <= maxDimensions; i++) {
			dimensions.push({
				dimensionName: null,
				dimensionKey: null,
				order: i,
			});
		}
		return dimensions;
	}

	// Create a map of order to filter
	const orderMap = new Map<number, any>();
	mergedGlobalFilters.forEach((filter) => {
		if (filter.order) {
			orderMap.set(filter.order, filter);
		}
	});

	// Create dimensions based on order (1 to maxDimensions)
	for (let order = 1; order <= maxDimensions; order++) {
		const filter = orderMap.get(order);

		if (filter) {
			dimensions.push({
				dimensionName: filter.field_name,
				dimensionKey: filter.field_key,
				order: order,
			});
		} else {
			// Add placeholder for missing order
			dimensions.push({
				dimensionName: null,
				dimensionKey: null,
				order: order,
			});
		}
	}

	return dimensions;
};

/**
 * Finds a section by its name
 * @param sections - Array of sections to search
 * @param sectionName - Name of the section to find
 * @returns The found section or undefined
 */
export const findSectionByName = (sections: ISection[], sectionName: string): ISection | undefined => {
	return sections.find((s) => s.section_name === sectionName);
};

/**
 * Validates if study data is ready for operations
 * @param studyId - The study ID to validate
 * @returns True if study ID is valid
 */
export const isStudyReady = (studyId?: string): boolean => {
	return Boolean(studyId && studyId.trim().length > 0);
};

/**
 * Validates if analysis is ready for operations
 * @param studyId - The study ID
 * @param analysisId - The analysis ID
 * @returns True if both IDs are valid
 */
export const isAnalysisReady = (studyId?: string, analysisId?: string | number): boolean => {
	return isStudyReady(studyId) && Boolean(analysisId);
};

/**
 * Safely converts analysis ID to consistent string format
 * @param analysisId - The analysis ID to normalize
 * @returns Normalized string representation
 */
export const normalizeAnalysisId = (analysisId: string | number | undefined): string => {
	if (analysisId === undefined || analysisId === null) {
		return "";
	}
	return String(analysisId);
};

/**
 * Checks if two analysis IDs match (handles type coercion)
 * @param id1 - First analysis ID
 * @param id2 - Second analysis ID
 * @returns True if IDs match
 */
export const isAnalysisIdEqual = (id1: string | number | undefined, id2: string | number | undefined): boolean => {
	return normalizeAnalysisId(id1) === normalizeAnalysisId(id2);
};

/**
 * Creates tab items from sections for Ant Design Tabs
 * @param sections - Array of sections
 * @returns Array of tab items
 */
export const createTabItemsFromSections = (sections: ISection[]) => {
	return sections.map((section) => ({
		key: section.section_name,
		label: section.section_name,
	}));
};

/**
 * Creates operations slot mapping for tabs
 * @param positions - Array of positions
 * @param operations - Record of operations by position
 * @returns Slot mapping object or null
 */
export const createOperationsSlot = (positions: PositionType[], operations: Record<PositionType, React.ReactNode>): Record<string, React.ReactNode> | null => {
	if (!positions.length) return null;
	return Object.fromEntries(positions.map((p) => [p, operations[p]]));
};

/**
 * Logs debug information in development mode only
 * @param message - Debug message
 * @param args - Additional arguments to log
 */
export const logDebug = (message: string, ...args: any[]) => {
	if (process.env.NODE_ENV === "development") {
		console.debug(`[Dashboard] ${sanitizeForLog(message)}`, ...args);
	}
};

/**
 * Error logger with context
 * @param context - Context where error occurred
 * @param error - Error object or message
 */
export const logError = (context: string, error: any) => {
	console.error(`[Dashboard Error - ${sanitizeForLog(context)}]:`, error);
};

/**
 * Extracts error message from various error types
 * @param error - Error object
 * @returns User-friendly error message
 */
export const getErrorMessage = (error: any): string => {
	if (typeof error === "string") {
		return error;
	}
	if (error?.message) {
		return error.message;
	}
	if (error?.response?.data?.message) {
		return error.response.data.message;
	}
	return "An unexpected error occurred";
};

export const prepareChartPayload = (filters: any, includeOrder?: boolean) => {
	return (
		filters?.map((filter) => ({
			is_custom_segment: filter?.is_custom_segment ?? false,
			split_by_field: filter.field_key,
			split_values: filter.selected_values,
			is_question: filter.is_question,
			is_numerical: filter.is_numerical,
			is_global: filter.is_global,
			...(includeOrder && { order: filter.order }),
		})) || []
	);
};

/**
 * Prepares split_by payload for multi-chart (scaffold) API calls.
 * Same as prepareChartPayload, but global filters are clamped to
 * a single selected value (multi-chart slides use single-select globals).
 */
export const prepareMultiChartPayload = (globalFilters: any[], localFilters: any[]): any[] => {
	const gfPayload =
		globalFilters?.map((filter) => ({
			is_custom_segment: false,
			split_by_field: filter.field_key,
			split_values: filter.selected_values?.length > 0 ? [filter.selected_values[0]] : [],
			is_question: filter.is_question,
			is_numerical: filter.is_numerical,
			is_global: filter.is_global,
			order: filter.order,
		})) || [];

	const lfPayload = prepareChartPayload(localFilters);

	return [...gfPayload, ...lfPayload];
};

export function updateDashboardDefaultAndOverriddenFiltersData(
	studyId: string,
	analysisId: string,
	defaultFilter: IDashboardCacheData,
	overriddenFilter: IDashboardCacheData,
	field_name: string,
	is_global: boolean,
	applyToAll: boolean,
	values: string[],
	dispatch: AppDispatch,
	field_key?: string, // Added optional field_key parameter
): { updatedDefaultFilterData: IDashboardCacheData; updatedOverridenFilterData: IDashboardCacheData } {
	let updatedDefaultFilterData: IDashboardCacheData = defaultFilter;
	let updatedOverridenFilterData: IDashboardCacheData = overriddenFilter;

	// Generate filter key based on field_key (preferred) or field_name and scope
	const filterIdentifier = field_key || field_name;
	const filterKey = is_global ? `${filterIdentifier}_Global` : `${filterIdentifier}___local`;

	// Create filter config object
	const filterConfig = {
		dimensionName: field_name,
		dimensionKey: field_key || field_name, // Store field_key if available
		selectedValues: values,
		isGlobal: is_global,
	};

	if (applyToAll) {
		// SCENARIO: Apply to All Analyses

		// 1. Update defaultFilters for the study - DEEP CLONE
		const updatedDefaultFilter: IDashboardCacheData = JSON.parse(JSON.stringify(defaultFilter));

		if (!updatedDefaultFilter[studyId]) {
			updatedDefaultFilter[studyId] = {};
		}

		updatedDefaultFilter[studyId][filterKey] = filterConfig;
		dispatch(setDefaultFilter(updatedDefaultFilter));
		updatedDefaultFilterData = updatedDefaultFilter;

		// 2. Remove this dimension from ALL overriddenFilters under this studyId - DEEP CLONE
		const cleanedOverriddenFilter: IDashboardCacheData = JSON.parse(JSON.stringify(overriddenFilter));

		if (cleanedOverriddenFilter[studyId]) {
			// Iterate through all analysis IDs under this study
			Object.keys(cleanedOverriddenFilter[studyId]).forEach((qId) => {
				if (cleanedOverriddenFilter[studyId][qId]?.[filterKey]) {
					delete cleanedOverriddenFilter[studyId][qId][filterKey];
				}

				// Clean up empty objects
				if (Object.keys(cleanedOverriddenFilter[studyId][qId]).length === 0) {
					delete cleanedOverriddenFilter[studyId][qId];
				}
			});

			// Clean up empty study object
			if (Object.keys(cleanedOverriddenFilter[studyId]).length === 0) {
				delete cleanedOverriddenFilter[studyId];
			}
		}

		dispatch(setOverridenFilter(cleanedOverriddenFilter));
		updatedOverridenFilterData = cleanedOverriddenFilter;
	} else {
		// SCENARIO: Apply (not to all) - only update overriddenFilters - DEEP CLONE
		const updatedOverriddenFilter: IDashboardCacheData = JSON.parse(JSON.stringify(overriddenFilter));

		if (!updatedOverriddenFilter[studyId]) {
			updatedOverriddenFilter[studyId] = {};
		}

		if (!updatedOverriddenFilter[studyId][analysisId]) {
			updatedOverriddenFilter[studyId][analysisId] = {};
		}

		updatedOverriddenFilter[studyId][analysisId][filterKey] = filterConfig;

		dispatch(setOverridenFilter(updatedOverriddenFilter));
		updatedOverridenFilterData = updatedOverriddenFilter;
	}

	return { updatedDefaultFilterData, updatedOverridenFilterData };
}

/**
 * Merges backend filters with cached user preferences (defaultFilter and overriddenFilter)
 * Priority: overriddenFilter > defaultFilter > backend selected_values
 */

export function mergeFiltersWithCache(
	backendFilters: IDashboardFilterDropdown[],
	studyId: string,
	analysisId: string,
	defaultFilter: IDashboardCacheData,
	overriddenFilter: IDashboardCacheData,
	isGlobal: boolean,
): IDashboardFilterDropdown[] {
	if (!backendFilters || backendFilters.length === 0) {
		return [];
	}

	return backendFilters.map((filter) => {
		const { field_name, field_key, field_values, selected_values: backendSelectedValues, mode } = filter;

		// Generate filter key based on field_key (preferred) or field_name and scope
		const filterIdentifier = field_key || field_name;
		const filterKey = isGlobal ? `${filterIdentifier}_Global` : `${filterIdentifier}___local`;

		let finalSelectedValues: string[] = backendSelectedValues || [];

		// Step 1: Check if dimension exists in overriddenFilter for this specific analysis
		const overriddenFilterForStudy = overriddenFilter?.[studyId];
		const overriddenFilterForAnalysis = overriddenFilterForStudy?.[analysisId];
		const overriddenDimension = overriddenFilterForAnalysis?.[filterKey];

		if (overriddenDimension) {
			// Priority 1: Use overridden selected values
			finalSelectedValues = overriddenDimension.selectedValues || [];
		} else {
			// Step 2: Check if dimension exists in defaultFilter for this study
			const defaultFilterForStudy = defaultFilter?.[studyId];
			const defaultDimension = defaultFilterForStudy?.[filterKey];

			if (defaultDimension) {
				// Priority 2: Use default selected values
				finalSelectedValues = defaultDimension.selectedValues || [];
			}
			// else: Priority 3: Use backend selected_values (already assigned above)
		}

		// Step 3: Validate that finalSelectedValues exist in field_values
		// Extract valid IDs from field_values array of objects
		const validIds = field_values.map((item) => item.id);
		const validatedSelectedValues = finalSelectedValues.filter((id) => validIds.includes(id));

		// Step 4: If mode is 'single' and multiple values exist, keep only the first one
		const finalValues = mode === "single" && validatedSelectedValues.length > 1 ? [validatedSelectedValues[0]] : validatedSelectedValues;

		// Return updated filter with validated selected values
		return {
			...filter,
			selected_values: finalValues,
		};
	});
}

export function mergeGlobalFiltersWithAnalysisLevel(
	analysisLevelGlobalFilters: IDashboardFilterDropdown[],
	defaultGlobalFilters: IDashboardFilterDropdown[],
	studyId: string,
	analysisId: string,
	defaultFilter: IDashboardCacheData,
	overriddenFilter: IDashboardCacheData,
	slideName?: string,
): IDashboardFilterDropdown[] {
	// If no defaultGlobalFilters, return empty array
	if (!defaultGlobalFilters || defaultGlobalFilters.length === 0) {
		return [];
	}

	// Create a map of analysis-level filters by field_key (preferred) or field_name for quick lookup
	const analysisFilterMap = new Map((analysisLevelGlobalFilters || []).map((filter) => [filter.field_key || filter.field_name, filter]));
	// Process each dimension in defaultGlobalFilters
	return defaultGlobalFilters.map((filter) => {
		const { field_name, field_key, field_values, selected_values: defaultSelectedValues, mode } = filter;

		// Generate filter key for global filters using field_key (preferred) or field_name
		const filterIdentifier = field_key || field_name;
		const filterKey = `${filterIdentifier}_Global`;

		let finalSelectedValues: string[] = defaultSelectedValues || [];

		// Priority 1: Check overriddenFilter for this studyId and analysisId
		const overriddenFilterForStudy = overriddenFilter?.[studyId];
		const overriddenFilterForAnalysis = overriddenFilterForStudy?.[analysisId];
		const overriddenDimension = overriddenFilterForAnalysis?.[filterKey];

		if (overriddenDimension && overriddenDimension.selectedValues) {
			// Found in overriddenFilter - use these values
			finalSelectedValues = overriddenDimension.selectedValues;
		} else {
			// Priority 2: Check defaultFilter for this studyId
			const defaultFilterForStudy = defaultFilter?.[studyId];
			const defaultDimension = defaultFilterForStudy?.[filterKey];

			if (defaultDimension && defaultDimension.selectedValues) {
				// Found in defaultFilter - use these values
				finalSelectedValues = defaultDimension.selectedValues;
			} else {
				// Priority 3: Check analysisLevelGlobalFilters for matching dimension
				const analysisLevelFilter = analysisFilterMap.get(filterIdentifier);
				if (analysisLevelFilter && analysisLevelFilter.selected_values) {
					// Found in analysis-level filters and no default global selection exists - use these values
					finalSelectedValues = analysisLevelFilter.selected_values;
				}

				// if (analysisLevelFilter && analysisLevelFilter.selected_values) {
				// 	// Found in analysis-level filters - use these values
				// 	finalSelectedValues = analysisLevelFilter.selected_values;
				// }
				// else: Priority 4 - Keep defaultGlobalFilters values (already assigned above)
			}
		}

		// Validate that finalSelectedValues exist in field_values
		// Extract valid IDs from field_values array of objects
		const validIds = field_values.map((item) => item.id);
		const validatedSelectedValues = finalSelectedValues.filter((id) => validIds.includes(id));
		const analysisLevelFilter = analysisFilterMap.get(filterIdentifier);
		let finalMode = analysisLevelFilter?.mode ?? mode;
		if (slideName && slideName.trim().length > 0) {
			finalMode = "single";
		}
		const finalValues =
			finalMode === "single" && validatedSelectedValues.length > 1 ? getFirstSortedSelectedValue(validatedSelectedValues, field_values) : validatedSelectedValues;
		// Return updated filter with validated selected values
		return {
			...filter,
			selected_values: finalValues,
			mode: finalMode,
		};
	});
}
export const formatNumber = (value: unknown, format: string = "##") => {
	if (!Number.isFinite(value)) return "";
	return numeral(value).format(format);
};

export const convertStringToNumeric = (value: unknown) => {
	if (value === null || value === undefined) return null;
	if (Number.isFinite(value)) return value;

	if (typeof value !== "string") return null;

	const trimmed = value.trim();
	if (!trimmed) return null;

	const numeric = trimmed.endsWith("%") ? trimmed.slice(0, -1) : trimmed;
	const parsed = Number(numeric);
	return Number.isFinite(parsed) ? parsed : null;
};

export const REM_BASE = 12;

export const pxToRem = (px: number): string => {
	if (!Number.isFinite(px)) return "0rem";
	const rem = px / REM_BASE;
	// Trim trailing zeros: 1.0000 → "1", 0.7500 → "0.75"
	return `${parseFloat(rem.toFixed(4))}rem`;
};
/**
 * Downloads the preview .pptx file and triggers a browser download.
 * Revokes the blob URL after download to prevent memory leaks.
 */
export const downloadPreviewFile = (previewFileUrl: string, fileName: string): void => {
	// Sanitize filename — strip path separators to prevent path traversal
	const sanitizedName = fileName.replace(/[/\\:*?"<>|]/g, "_") || "preview_template.pptx";

	const link = document.createElement("a");
	link.href = previewFileUrl;
	link.download = sanitizedName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

/**
 * Returns the first selected value sorted by its position in field_values.
 * Ensures multi-chart single-select mode always shows the earliest
 * option from the field's natural order, not the chronological selection order.
 */
export const getFirstSortedSelectedValue = (selectedValues: string[], fieldValues: IFilterValues[]): string[] => {
	if (!selectedValues?.length) return [];
	if (selectedValues.length === 1) return selectedValues;

	const fieldOrder = new Map(fieldValues.map((item, idx) => [item.id, idx]));

	const sorted = [...selectedValues].sort((a, b) => {
		const idxA = fieldOrder.get(a) ?? Number.MAX_SAFE_INTEGER;
		const idxB = fieldOrder.get(b) ?? Number.MAX_SAFE_INTEGER;
		return idxA - idxB;
	});

	return [sorted[0]];
};
